import {
  Component,
  Prop,
  Event,
  EventEmitter,
  Method,
  State,
  Element,
  h
} from '@stencil/core';
import { Color, Size } from '../../interface';
import { DataIn, DataOut } from './inner-range-inception.interface';

@Component({
  tag: 'lar-inner-range-inception-area',
  styleUrl: 'inner-range-inception-area.scss',
  shadow: true
})
export class InnerRangeArea {
  @Element() el!: HTMLLarInnerRangeAreaElement;

  /////// LarvaNode base properties
  /**
   * Component main icon
   */
  @Prop({ mutable: true }) icon?: string = 'security';
  /**
   * The color to use from your application's color palette.
   * Options are: `"primary"`, `"secondary"`, `"tertiary"`, `"success"`, `"warning"`, `"danger"`, `"light"`, `"medium"`, and `"dark"`.
   */
  @Prop({ mutable: true }) color?: Color = 'primary';
  /**
   * The color to use from your application's color palette for Components modal window.
   */
  @Prop({ mutable: true }) colorModal?: Color;
  /**
   * The color to use from your application's color palette for inputs
   */
  @Prop({ mutable: true }) colorInputs?: Color;
  /**
   * The color to use from your application's color palette for indication icon
   */
  @Prop() colorIconSmall?: Color;
  /**
   * Allow node indication color automatic change based on feedback/node value. Defaults to false
   */
  @Prop() allowIndicationAutoColoring: boolean = false;
  /**
   * Allow node color automatic change based on feedback/node value. Defaults to true
   */
  @Prop() allowNodeAutoColoring: boolean = true;
  /**
   * Hide node titles
   */
  @Prop() hideTitles?: boolean;
  /**
   * Component superscript title
   */
  @Prop() supTitle?: string;
  /**
   * Component subtitle
   */
  @Prop() subTitle?: string;
  /**
   * Is logging for this component enabled (lar-log subcomponent loaded)
   */
  @Prop() log: boolean = false;
  /**
   * Component main title
   */
  @Prop() mainTitle!: string;
  /**
   * Node size
   */
  @Prop() nodeSize?: Size = 'default';
  /**
   * @see {@link ../readme.md} chapter "Components input and output" for further information.
   */
  @Event() output!: EventEmitter<DataOut>;
  /**
   * @see {@link ../readme.md} chapter "Sub-Components requests and responses" for further information.
   */
  @Event() request!: EventEmitter;
  /////// LarvaNode base properties and events - end

  @State() armed: boolean = false;
  @State() armReady: boolean = false;
  @State() alarm: boolean = false;
  @State() inceptionError: boolean = false;
  @State() loading: boolean = true;
  
  private node!: HTMLLarNodeElement;

  /**
   * Larva error input
   */
  @Method()
  async error(data: any) {
    if (this.node) {
      this.node.error(data);
    }
    this.loading = false;
  }
  /**
   * Larva input message
   */
  @Method()
  async input(data: DataIn) {
    this.armed = !!data?.armed
    this.alarm = !!data?.alarm;
    this.armReady = !!data?.armReady;
    this.inceptionError = !data || 
    !!data?.connencting || !!data?.error;
    this.loading = false;
  }

  componentDidLoad() {
    const el = this.el.shadowRoot || this.el;
    this.node = el.querySelector('lar-node') as HTMLLarNodeElement;
    this.loading = true;
    this.output.emit();
  }

  armDisarm() {
    this.loading = true;
    return this.output.emit({
      areaControlType: this.armed ? 'Disarm' : 'Arm'
    });
  }

  render() {
    const iconSmall = (this.inceptionError || this.alarm) ? 'warning' : (this.armed ? 'lock' : 'unlock');
    let colorIconSmall = this.colorIconSmall;
    let value = this.loading ? ' ' : (this.armed ? 'area.armed' : 'area.disarmed');
    if (this.inceptionError) {
      value = 'area.systemTroubles';
      colorIconSmall = this.allowIndicationAutoColoring ? 'warning' : colorIconSmall;
    }
    if (this.alarm) {
      value = 'area.alarm';
      colorIconSmall = this.allowIndicationAutoColoring ? 'danger' : colorIconSmall;
    }
    const componentProps = {
      onClick: () => this.armDisarm(),
      value: this.armed ? 'area.disarm' : 'area.arm',
      icon: this.armed ? 'unlock' : 'lock',
      disabled: this.loading || !this.armReady || this.inceptionError,
      armReady: this.armReady,
      color: this.colorInputs || this.color
    };
    return [
      <lar-node
        value={value}
        hideTitles={this.hideTitles}
        iconSmall={iconSmall}
        icon={this.icon}
        colorIconSmall={colorIconSmall}
        color={this.alarm && this.allowNodeAutoColoring === true ? 'danger' : this.color}
        supTitle={this.supTitle}
        mainTitle={this.mainTitle}
        subTitle={this.subTitle}
        colorModal={this.alarm && this.allowNodeAutoColoring === true ? 'danger' : this.colorModal}
        colorInputs={this.colorInputs}
        log={this.log}
        loading={this.loading}
        nodeSize={this.nodeSize}
        component="lar-inner-range-inception-area-content"
        componentProps={componentProps}
        >
        <slot />
      </lar-node>
    ];
  }
}
